import { type FC, Suspense, useEffect, useMemo } from 'react';
import { useQueryLoader } from 'react-relay';
import { dibsLazy } from 'dibs-lazy/exports/dibsLazy.ts';
import { trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2.ts';

import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

import {
    pdpSharedRecentlyFavoritedRootQuery,
    type pdpSharedRecentlyFavoritedRootQueryType,
} from '../../../queries/pdpSharedRecentlyFavoritedRoot.ts';
import { PdpRespMarketplaceLoadingCarousel } from '../../respMarketplace/PdpRespMarketplaceLoadingCarousel/PdpRespMarketplaceLoadingCarousel.tsx';
import { PdpMobileMarketplaceLoadingCarousel } from '../../mobileMarketplace/PdpMobileMarketplaceLoadingCarousel/PdpMobileMarketplaceLoadingCarousel.tsx';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import {
    isRecentlyFavoritedPdpVariant as isRecentlyFavoritedPdpVariantFn,
    PDP_RecentFavesMostSaved_Feb2026,
} from '../../../utils/isRecentlyFavoritedHelper.ts';

// lazy components
const PdpSharedRecentlyFavoritedCarouselLazy = dibsLazy(
    'PdpSharedRecentlyFavoritedCarousel',
    () =>
        import(
            /* webpackChunkName: "PdpSharedRecentlyFavoritedCarousel" */ './PdpSharedRecentlyFavoritedCarousel.tsx'
        )
);

type Props = {
    userId: string;
    itemId: string;
    isTrade: boolean;
    priceBookName: string;
    fetchUser: boolean;
    currency: string;
};

const LoadingCarousel: FC = () => {
    const { isMobile } = useServerVarsContext();
    return isMobile ? (
        <PdpMobileMarketplaceLoadingCarousel />
    ) : (
        <PdpRespMarketplaceLoadingCarousel />
    );
};

export const PdpSharedRecentlyFavoritedCarouselRenderer: FC<Props> = ({
    userId,
    itemId,
    isTrade,
    priceBookName,
    fetchUser,
    currency,
}) => {
    const [queryRef, loadRelayQuery] = useQueryLoader<pdpSharedRecentlyFavoritedRootQueryType>(
        pdpSharedRecentlyFavoritedRootQuery
    );

    const isRecentlyFavoritedPdpVariant = useMemo(() => isRecentlyFavoritedPdpVariantFn(), []);

    useEffect(() => {
        if (isRecentlyFavoritedPdpVariant) {
            loadRelayQuery({
                userId,
                itemId,
                isTrade,
                priceBookName,
                fetchUser,
            });
        }
    }, [
        loadRelayQuery,
        userId,
        itemId,
        isTrade,
        priceBookName,
        fetchUser,
        isRecentlyFavoritedPdpVariant,
    ]);

    return (
        <>
            <VisibilityTracker
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackAbTestV2Variant(PDP_RecentFavesMostSaved_Feb2026);
                    }
                }}
            />
            {isRecentlyFavoritedPdpVariant && (
                <>
                    {queryRef ? (
                        <Suspense fallback={<LoadingCarousel />}>
                            <PdpSharedRecentlyFavoritedCarouselLazy
                                queryRef={queryRef}
                                currency={currency}
                            />
                        </Suspense>
                    ) : (
                        <LoadingCarousel />
                    )}
                </>
            )}
        </>
    );
};
